import { useState, useRef, useEffect } from "react";
import { ScanLine, ArrowRight, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, PrioriteBadge, StatutBadge, DelaiBadge } from "@/components/shared";
import { STATUT_ORDER, STATUT_LABELS, STATUT_OPTIONNELS } from "@/lib/rbac";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const CAN_ADVANCE = ["technicien", "chef_service", "pathologiste", "pathologiste_distant"];
const ETAPES_TECH = ["macroscopie", "inclusion", "coupe", "coloration", "ihc", "moleculaire", "lecture"];

export default function Paillasse() {
  const { user } = useAuth();
  const canAdvance = CAN_ADVANCE.includes(user?.role);
  const [numero, setNumero] = useState("");
  const [dem, setDem] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const scan = async (e) => {
    e?.preventDefault?.();
    const n = numero.trim();
    if (!n) return;
    setBusy(true); setDem(null);
    try {
      const { data } = await api.get(`/demandes/by-numero/${encodeURIComponent(n)}`);
      setDem(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Numéro introuvable");
    } finally { setBusy(false); }
  };

  const setStatut = async (statut) => {
    if (!dem) return;
    setBusy(true);
    try {
      await api.patch(`/demandes/${dem.id}/statut`, { statut });
      toast.success(`Statut → ${STATUT_LABELS[statut] || statut}`);
      const { data } = await api.get(`/demandes/by-numero/${encodeURIComponent(dem.numero)}`);
      setDem(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Changement impossible");
    } finally { setBusy(false); }
  };

  const reset = () => { setDem(null); setNumero(""); inputRef.current?.focus(); };

  return (
    <div data-testid="paillasse-page">
      <PageHeader title="Paillasse technique"
                  subtitle="Scannez ou saisissez le numéro de demande pour faire avancer les étapes." />

      <Card className="p-4 border-slate-200 max-w-2xl">
        <form onSubmit={scan} className="flex gap-2">
          <div className="relative flex-1">
            <ScanLine className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input ref={inputRef} data-testid="scan-input" value={numero}
                   onChange={(e) => setNumero(e.target.value)} placeholder="DEM-2026-0001"
                   className="pl-9 font-mono uppercase" autoComplete="off" />
          </div>
          <Button type="submit" data-testid="scan-submit" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-1">Rechercher</span>
          </Button>
        </form>
      </Card>

      {dem && (
        <Card className="p-5 border-slate-200 mt-4 max-w-3xl" data-testid="scan-result">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="font-head text-lg font-bold text-slate-900">{dem.numero}</div>
              <div className="text-sm text-slate-500">{dem.type_examen} · {dem.patient?.nom || ""}</div>
            </div>
            <div className="flex items-center gap-2">
              <PrioriteBadge value={dem.priorite} />
              <StatutBadge value={dem.statut} />
              <DelaiBadge value={dem.delai} cible={dem.cible_label} />
            </div>
          </div>

          <div className="flex flex-wrap gap-1 mt-4" data-testid="scan-stepper">
            {STATUT_ORDER.map((s) => {
              const idx = STATUT_ORDER.indexOf(dem.statut);
              const i = STATUT_ORDER.indexOf(s);
              const done = idx >= 0 && i < idx, cur = i === idx;
              return (
                <span key={s} className={`px-2.5 py-1 rounded-full text-xs border ${cur ? "bg-sky-600 text-white border-sky-600" : done ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-white text-slate-400 border-slate-200"}`}>
                  {STATUT_LABELS[s]}
                </span>
              );
            })}
          </div>

          {canAdvance ? (
            <>
              {dem.prochaine_etape && (
                <Button className="mt-5" data-testid="advance-next-btn" disabled={busy}
                        onClick={() => setStatut(dem.prochaine_etape)}>
                  <ArrowRight className="h-4 w-4 mr-1" /> Étape suivante : {STATUT_LABELS[dem.prochaine_etape]}
                </Button>
              )}
              <div className="mt-4">
                <div className="text-xs font-semibold text-slate-500 mb-2">Marquer une étape technique</div>
                <div className="flex flex-wrap gap-2">
                  {ETAPES_TECH.map((s) => (
                    <Button key={s} variant="outline" size="sm" data-testid={`statut-${s}-btn`}
                            disabled={busy || s === dem.statut} onClick={() => setStatut(s)}>
                      {STATUT_LABELS[s]}{STATUT_OPTIONNELS.includes(s) ? " (opt.)" : ""}
                    </Button>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="mt-4 text-sm text-slate-500">Votre rôle ne permet pas de modifier les statuts techniques.</div>
          )}

          <button onClick={reset} data-testid="scan-reset" className="mt-5 text-xs text-sky-600 hover:underline">
            Scanner une autre demande
          </button>
        </Card>
      )}
    </div>
  );
}
